import {HttpClient, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {MatSnackBar} from '@angular/material/snack-bar';
import {BaseService} from '@nh/angular-tses';
import {DateService} from 'app/core/services/date.service';
import {environment} from 'environments/environment';
import * as FileSaver from 'file-saver';
import {BehaviorSubject, Observable} from 'rxjs';
import {SearchColumn} from './query/enums/search-column';
import {SearchResult} from './query/enums/search-result';
import {DasqlNode} from './query/nodes/dasql-node';

export interface PaginationAndSortData {
  pageIndex: number;
  pageSize: number;
  columnSelected: string;
  direction: string;
}

@Injectable({
  providedIn: 'root'
})
export class SearchService extends BaseService {
  public static readonly SEARCH_ENDPOINT: string = `${environment.tsesConfig.apiHost}/search/query`;
  public static readonly CSV_ENDPOINT: string = `${environment.tsesConfig.apiHost}/csv/query`;
  public static readonly CONVERT_DECIMAL_REVISION_TO_INT = 1000000;

  public static readonly DEFAULT_RESULT_COLUMNS: SearchColumn[] = [
    SearchColumn.DEPARTURE_ID_NUMBER_AND_REVISION,
    SearchColumn.DEPARTURE_ID_NUMBER,
    SearchColumn.REVISION,
    SearchColumn.TITLE,
    SearchColumn.WORK_FLOW_STATE,
    SearchColumn.DESIGN_ORGANISATION,
    SearchColumn.DESCRIPTION,
    SearchColumn.SUBJECT,
    SearchColumn.PIN,
    SearchColumn.SCHEME_NAME
  ];

  private emptyMessageSubscriber = new BehaviorSubject<string>('no Results');

  constructor(protected _snackbar: MatSnackBar, private _httpClient: HttpClient, private dateService: DateService) {
    super(_snackbar);
  }

  public setEmptyMessageSubscriber(emptyMessage: string) {
    this.emptyMessageSubscriber.next(emptyMessage);
  }

  public getEmptyMessageSubscriber(): Observable<string> {
    return this.emptyMessageSubscriber.asObservable();
  }

  public search(
    query: string | DasqlNode,
    columns: SearchColumn[],
    paginationAndSortData?: PaginationAndSortData
  ): Promise<SearchResult> {
    return typeof query === 'string'
      ? this.searchWithString(query, columns, paginationAndSortData)
      : this.searchWithNode(query, columns, paginationAndSortData);
  }

  private searchWithNode(
    node: DasqlNode,
    columns: SearchColumn[],
    paginationAndSortData?: PaginationAndSortData
  ): Promise<SearchResult> {
    if (!node) {
      return Promise.resolve({totalNumberOfResults: 0, results: []});
    }

    const query: string = node.emit();
    return this.searchWithString(query, columns, paginationAndSortData);
  }

  private searchWithString(
    query: string,
    columns: SearchColumn[],
    paginationAndSortData: PaginationAndSortData
  ): Promise<SearchResult> {
    if (!query || query.trim().length === 0) {
      return Promise.resolve({totalNumberOfResults: 0, results: []});
    }

    return this._httpClient
      .post(SearchService.SEARCH_ENDPOINT, {query, columns, paginationAndSortData})
      .toPromise()
      .then((response: SearchResult) => {
        return response;
      })
      .catch((err: HttpErrorResponse) => {
        this._handleError(err, 'Sorry, could not make search query. Please try again.', 'search-error');
        return Promise.reject(err);
      });
  }

  public export(query: string | DasqlNode, columns: SearchColumn[]): Promise<any> {
    return typeof query === 'string' ? this.exportWithString(query, columns) : this.exportWithNode(query, columns);
  }

  private exportWithNode(node: DasqlNode, columns: SearchColumn[]): Promise<any> {
    const query: string = node.emit();
    return this.exportWithString(query, columns);
  }

  private exportWithString(query: string, columns: SearchColumn[]): Promise<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'text/csv',
        'Content-Type': 'application/json'
      }),
      responseType: 'blob' as 'blob'
    };

    return this._httpClient
      .post(SearchService.CSV_ENDPOINT, {query, columns}, httpOptions)
      .toPromise()
      .then((response: Blob) => FileSaver.saveAs(response, `${this.generateFileName()}.csv`))
      .catch((err: HttpErrorResponse) => {
        this._handleError(
          err,
          'Failed to export search results.  Please try again or contact the admin team.',
          'search-error'
        );
        return Promise.reject(err);
      });
  }

  private generateFileName() {
    const now: Date = new Date();
    return (
      `${this.dateService.formatDate(now, 'YYYY-MM-DD')}_` +
      `${this.dateService.formatTime(now, 'HH-mm')}_DAS_search_results`
    );
  }
}
